import client from './client';
import type { ApiResponse, User } from '../types';

interface LoginPayload {
  email: string;
  password: string;
}

interface LoginResult {
  accessToken: string;
  refreshToken: string;
  user: User;
}

const toAppUser = (user: any): User => {
  const nameParts = String(user?.name ?? '').split(' ').filter(Boolean);
  return {
    ...(user ?? {}),
    firstName: nameParts[0] ?? '',
    lastName: nameParts.slice(1).join(' '),
    avatar: typeof user?.avatar_path === 'string' ? user.avatar_path : undefined,
  };
};

interface RegisterPayload {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone?: string;
}

export const authApi = {
  login: async (data: LoginPayload): Promise<LoginResult> => {
    const response = await client.post<{ message: string; user: User; token: string }>('/auth/login', data);
    return {
      user: toAppUser(response.data.user),
      accessToken: response.data.token,
      refreshToken: response.data.token,
    };
  },

  register: (data: RegisterPayload) =>
    client.post<ApiResponse<User>>('/auth/register', data),

  logout: () => client.post<ApiResponse<void>>('/auth/logout'),

  refreshToken: (refreshToken: string) =>
    client.post<ApiResponse<LoginResult>>('/auth/refresh', { refreshToken }),

  forgotPassword: (email: string) =>
    client.post<ApiResponse<void>>('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    client.post<ApiResponse<void>>('/auth/reset-password', { token, password }),

  changePassword: (currentPassword: string, newPassword: string) =>
    client.post<ApiResponse<void>>('/auth/change-password', { currentPassword, newPassword }),

  getProfile: async (): Promise<User> => {
    const response = await client.get<{ user: User }>('/auth/me');
    return toAppUser(response.data.user);
  },

  updateProfile: (data: Partial<User>) =>
    client.put<ApiResponse<User>>('/auth/profile', data),

  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return client.post<ApiResponse<{ url: string }>>('/auth/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};
